/**
 * Bulk Upload — Smart Worksheet Parser
 *
 * Enhanced parsing pipeline that handles real-world WBG spreadsheets:
 * - Header row auto-detection (headers not always on row 1)
 * - Merged cell unmerging (propagate values)
 * - Context row detection (single-value rows above data that set defaults)
 *
 * Works alongside the existing parseExcel() in utils/files.ts but adds
 * the extra intelligence needed for bulk upload.
 */

import * as ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { CsvRow } from '../../../../@types';
import {
	ContextRow,
	ContextRowEntry,
	ContextDefault,
	HeaderDetection,
	UploadFieldDef,
	LookupData,
} from './types';
import { normalize, scoreRelation } from './resolve';

// ---------------------------------------------------------------------------
// Header Row Detection
// ---------------------------------------------------------------------------

interface RowScore {
	rowNumber: number;
	score: number;
	stringRatio: number;
	uniqueCount: number;
	populatedCount: number;
	totalCols: number;
}

/**
 * Score each row as a potential header row.
 * Heuristics:
 * - High % of string-type cells (not numbers/dates)
 * - Most cells populated (low empty count)
 * - High unique value count (headers are distinct)
 * - Bonus if the next row looks like data (numbers, dates, different types)
 */
function scoreRow(worksheet: ExcelJS.Worksheet, rowNumber: number): RowScore {
	const row = worksheet.getRow(rowNumber);
	let populated = 0;
	let stringCells = 0;
	const values = new Set<string>();
	let totalCols = 0;

	row.eachCell({ includeEmpty: false }, (cell) => {
		totalCols++;
		const val = getCellValue(cell);
		if (val !== null && val !== undefined && String(val).trim() !== '') {
			populated++;
			values.add(String(val).trim().toLowerCase());
			if (typeof val === 'string' || typeof cell.value === 'string') {
				stringCells++;
			} else if (
				cell.type === ExcelJS.ValueType.String ||
				cell.type === ExcelJS.ValueType.RichText
			) {
				stringCells++;
			}
		}
	});

	if (populated === 0)
		return { rowNumber, score: 0, stringRatio: 0, uniqueCount: 0, populatedCount: 0, totalCols };

	const stringRatio = stringCells / populated;
	const uniqueRatio = values.size / populated;
	const fillRatio = populated / Math.max(totalCols, 1);

	// Weighted score
	let score = stringRatio * 40 + uniqueRatio * 30 + fillRatio * 20;

	// Bonus: at least 3 populated cells (avoid matching single-cell title rows)
	if (populated >= 3) score += 10;

	return {
		rowNumber,
		score,
		stringRatio,
		uniqueCount: values.size,
		populatedCount: populated,
		totalCols,
	};
}

/**
 * Auto-detect which row contains the headers.
 * Returns the best candidate with confidence.
 */
export function detectHeaderRow(worksheet: ExcelJS.Worksheet, maxScan = 15): HeaderDetection {
	const rowCount = Math.min(worksheet.rowCount, maxScan);
	const scores: RowScore[] = [];

	for (let r = 1; r <= rowCount; r++) {
		scores.push(scoreRow(worksheet, r));
	}

	// Filter out completely empty rows
	const candidates = scores.filter((s) => s.score > 0);

	if (candidates.length === 0) {
		return { headerRow: 1, dataStartRow: 2, confidence: 0, preHeaderRows: [] };
	}

	// Sort by score descending
	candidates.sort((a, b) => b.score - a.score);
	const best = candidates[0];

	// Confidence: how much better is the best vs second-best
	const secondBest = candidates[1];
	const gap = secondBest ? (best.score - secondBest.score) / best.score : 1;
	const confidence = Math.min(gap + (best.score / 100) * 0.5, 1);

	// Gather pre-header rows for context-row detection
	const preHeaderRows: Array<{ rowNumber: number; content: string }> = [];
	for (let r = 1; r < best.rowNumber; r++) {
		const row = worksheet.getRow(r);
		const values: string[] = [];
		row.eachCell({ includeEmpty: false }, (cell) => {
			const val = getCellValue(cell);
			if (val !== null && val !== undefined && String(val).trim() !== '') {
				values.push(String(val).trim());
			}
		});
		if (values.length > 0) {
			preHeaderRows.push({ rowNumber: r, content: values.join(' | ') });
		}
	}

	return {
		headerRow: best.rowNumber,
		dataStartRow: best.rowNumber + 1,
		confidence,
		preHeaderRows,
	};
}

// ---------------------------------------------------------------------------
// Merged Cell Handling
// ---------------------------------------------------------------------------

/**
 * Unmerge all merged cells, propagating the top-left value to all cells in the range.
 * This mutates the worksheet in-place.
 */
export function unmergeCells(worksheet: ExcelJS.Worksheet): void {
	// ExcelJS stores merged cells as an object with range keys
	const merges = (
		worksheet as unknown as {
			_merges?: Record<string, { left: number; right: number; top: number; bottom: number }>;
		}
	)._merges;
	if (!merges) return;

	const mergeKeys = Object.keys(merges);
	for (const key of mergeKeys) {
		const range = merges[key];
		if (!range) continue;

		// Get the value from the top-left cell
		const topLeft = worksheet.getCell(range.top, range.left);
		const value = topLeft.value;

		// Propagate to all cells in the merge range
		for (let r = range.top; r <= range.bottom; r++) {
			for (let c = range.left; c <= range.right; c++) {
				if (r === range.top && c === range.left) continue;
				worksheet.getCell(r, c).value = value;
			}
		}
	}

	// Unmerge everything
	for (const key of mergeKeys) {
		try {
			worksheet.unMergeCells(key);
		} catch {
			// Some merge ranges may fail to unmerge cleanly; that's fine
		}
	}
}

// ---------------------------------------------------------------------------
// Context Row Detection
// ---------------------------------------------------------------------------

/**
 * Detect context rows: rows before the header that contain label-value pairs
 * setting defaults for the data below.
 *
 * Pattern: A row with 1-2 populated cells, often "Label: Value" or
 * cells in known label/value column pairs.
 */
export function detectContextRows(worksheet: ExcelJS.Worksheet, headerRow: number): ContextRow[] {
	const contextRows: ContextRow[] = [];

	for (let r = 1; r < headerRow; r++) {
		const row = worksheet.getRow(r);
		const populated: Array<{ col: number; value: string }> = [];

		row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
			const val = getCellValue(cell);
			if (val !== null && val !== undefined && String(val).trim() !== '') {
				populated.push({ col: colNumber, value: String(val).trim() });
			}
		});

		if (populated.length === 0) continue;

		// Skip rows that look like titles (single cell, likely just a heading)
		if (populated.length === 1) {
			// Check if it contains a colon (label: value in one cell)
			const colonIdx = populated[0].value.indexOf(':');
			if (colonIdx > 0 && colonIdx < populated[0].value.length - 1) {
				const label = populated[0].value.substring(0, colonIdx).trim();
				const value = populated[0].value.substring(colonIdx + 1).trim();
				if (label && value) {
					contextRows.push({
						rowNumber: r,
						entries: [{ label, value, labelCol: populated[0].col, valueCol: populated[0].col }],
					});
				}
			}
			continue;
		}

		// Two or more populated cells: treat as label-value pairs
		const entries: ContextRowEntry[] = [];
		for (let i = 0; i < populated.length - 1; i += 2) {
			const labelCell = populated[i];
			const valueCell = populated[i + 1];
			if (labelCell && valueCell) {
				// Label should look like a label (not purely numeric)
				if (isNaN(Number(labelCell.value))) {
					entries.push({
						label: labelCell.value.replace(/:$/, '').trim(),
						value: valueCell.value,
						labelCol: labelCell.col,
						valueCol: valueCell.col,
					});
				}
			}
		}
		// If odd number of cells, the last unpaired one might be a standalone value
		if (entries.length > 0) {
			contextRows.push({ rowNumber: r, entries });
		}
	}

	return contextRows;
}

/**
 * Match detected context rows to schema fields and resolve their values
 * (including relational values through the scoring pipeline).
 */
export function resolveContextRowDefaults(
	contextRows: ContextRow[],
	fields: UploadFieldDef[],
	lookupData?: LookupData
): ContextDefault[] {
	const defaults: ContextDefault[] = [];

	for (const ctxRow of contextRows) {
		for (const entry of ctxRow.entries) {
			// Try to match the label to a field
			const normalizedLabel = normalize(entry.label);
			const matchedField = fields.find((f) => {
				const aliases = [f.name, f.label, ...(f.headerAliases ?? [])];
				return aliases.some((a) => normalize(a) === normalizedLabel);
			});

			if (!matchedField) continue;

			const ctxDefault: ContextDefault = {
				label: entry.label,
				targetField: matchedField.name,
				rawValue: entry.value,
				resolvedValue: entry.value,
				confirmed: false, // User must confirm in the UI
			};

			// For relation fields, run through scoring pipeline
			if (matchedField.dataType === 'relation' && matchedField.relation && lookupData) {
				const items = lookupData[matchedField.relation.contextKey];
				if (items) {
					const scoreResult = scoreRelation(entry.value, matchedField, items);
					ctxDefault.relation = {
						resolvedId: scoreResult.resolvedId,
						resolvedLabel: scoreResult.candidates[0]?.label ?? null,
						confidence: scoreResult.confidence,
						candidates: scoreResult.candidates,
					};
					if (scoreResult.resolvedId !== null) {
						ctxDefault.resolvedValue = scoreResult.resolvedId;
					}
				}
			}

			defaults.push(ctxDefault);
		}
	}

	return defaults;
}

// ---------------------------------------------------------------------------
// Full Parse Pipeline
// ---------------------------------------------------------------------------

export interface ParseResult {
	headers: string[];
	rows: CsvRow[];
	headerDetection: HeaderDetection;
	contextRows: ContextRow[];
	contextDefaults: ContextDefault[];
	sheetNames: string[];
	selectedSheet: string;
}

export interface ParseOptions {
	/** Which sheet to parse (defaults to first) */
	sheetName?: string;
	/** Override: force header row (user adjustment from Step 2) */
	headerRow?: number;
	/** Override: force data start row */
	dataStartRow?: number;
	/** Schema fields for context row resolution */
	fields?: UploadFieldDef[];
	/** Lookup data for relation resolution */
	lookupData?: LookupData;
}

/**
 * Smart parse pipeline — replaces the basic parseExcel for bulk upload use cases.
 *
 * 1. Load workbook and select sheet
 * 2. Unmerge merged cells
 * 3. Detect header row (or use override)
 * 4. Detect context rows above the header
 * 5. Extract headers and data rows
 * 6. Resolve context row defaults
 */
export async function parseWorksheet(file: File, options: ParseOptions = {}): Promise<ParseResult> {
	// CSV files: use Papa Parse instead of ExcelJS
	const isCsv = /\.(csv|tsv|txt)$/i.test(file.name);
	if (isCsv) {
		return parseCsvFile(file, options);
	}

	const arrayBuffer = await file.arrayBuffer();
	const workbook = new ExcelJS.Workbook();
	await workbook.xlsx.load(arrayBuffer);

	const sheetNames = workbook.worksheets.map((ws) => ws.name);
	const selectedSheet = options.sheetName || sheetNames[0];
	const worksheet = workbook.getWorksheet(selectedSheet);

	if (!worksheet) {
		throw new Error(`Sheet "${selectedSheet}" not found`);
	}

	// Step 1: Unmerge merged cells
	unmergeCells(worksheet);

	// Step 2: Detect or use provided header row
	const headerDetection = options.headerRow
		? {
				headerRow: options.headerRow,
				dataStartRow: options.dataStartRow ?? options.headerRow + 1,
				confidence: 1,
				preHeaderRows: [],
			}
		: detectHeaderRow(worksheet);

	const headerRowNum = headerDetection.headerRow;
	const dataStartRow = headerDetection.dataStartRow;

	// Step 3: Detect context rows (above header)
	const contextRows = detectContextRows(worksheet, headerRowNum);

	// Step 4: Extract headers
	const headers: string[] = [];
	const headerRowObj = worksheet.getRow(headerRowNum);
	headerRowObj.eachCell({ includeEmpty: true }, (cell, colNumber) => {
		const val = getCellValue(cell);
		headers[colNumber - 1] = val !== null && val !== undefined ? String(val).trim() : '';
	});

	// Check for hidden metadata row (row after header, starting with __field:)
	const metaRowNum = headerRowNum + 1;
	if (metaRowNum < dataStartRow || metaRowNum <= worksheet.rowCount) {
		const metaRow = worksheet.getRow(metaRowNum);
		const firstCell = getCellValue(metaRow.getCell(1));
		if (firstCell && String(firstCell).startsWith('__field:')) {
			// This is a metadata row from our template — use it for precise matching
			metaRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
				const val = getCellValue(cell);
				if (val && String(val).startsWith('__field:')) {
					headers[colNumber - 1] = String(val).trim();
				}
			});
			// Adjust data start row past the metadata row
			headerDetection.dataStartRow = Math.max(headerDetection.dataStartRow, metaRowNum + 1);
		}
	}

	// Filter out empty trailing headers
	while (headers.length > 0 && headers[headers.length - 1] === '') {
		headers.pop();
	}

	// Step 5: Extract data rows
	const rows: CsvRow[] = [];
	const actualDataStart = headerDetection.dataStartRow;

	for (let r = actualDataStart; r <= worksheet.rowCount; r++) {
		const rowObj = worksheet.getRow(r);
		const row: CsvRow = {};
		let hasValue = false;

		for (let c = 0; c < headers.length; c++) {
			const header = headers[c];
			if (!header) continue;

			const cell = rowObj.getCell(c + 1);
			const val = getCellValue(cell);

			if (val !== null && val !== undefined) {
				let strVal: string;
				// Handle ExcelJS rich text, hyperlinks, etc.
				if (typeof val === 'object' && 'text' in (val as object)) {
					strVal = String((val as { text?: unknown }).text || '');
				} else {
					strVal = String(val);
				}
				row[header] = strVal;
				if (strVal.trim() !== '') hasValue = true;
			} else {
				row[header] = '';
			}
		}

		// Only add non-empty rows
		if (hasValue) {
			rows.push(row);
		}
	}

	// Step 6: Resolve context defaults
	const contextDefaults = options.fields
		? resolveContextRowDefaults(contextRows, options.fields, options.lookupData)
		: [];

	return {
		headers: headers.filter(Boolean),
		rows,
		headerDetection,
		contextRows,
		contextDefaults,
		sheetNames,
		selectedSheet,
	};
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Safely extract a primitive value from an ExcelJS cell */
function getCellValue(cell: ExcelJS.Cell): unknown {
	const val = cell.value;
	if (val === null || val === undefined) return null;

	// Handle ExcelJS value types
	if (typeof val === 'object') {
		// Rich text
		if ('richText' in (val as object)) {
			return (val as unknown as { richText: { text?: string }[] }).richText
				.map((rt) => rt.text || '')
				.join('');
		}
		// Hyperlink
		if ('text' in (val as object)) {
			return (val as unknown as { text: unknown }).text;
		}
		// Formula result
		if ('result' in (val as object)) {
			return (val as unknown as { result: unknown }).result;
		}
		// SharedString
		if ('sharedString' in (val as object)) {
			return (val as unknown as { sharedString: unknown }).sharedString;
		}
		// Date
		if (val instanceof Date) {
			return val;
		}
	}

	return val;
}

// ---------------------------------------------------------------------------
// CSV Parsing
// ---------------------------------------------------------------------------

/**
 * Parse a CSV/TSV file using Papa Parse.
 * Returns the same ParseResult shape as the Excel path.
 */
async function parseCsvFile(file: File, options: ParseOptions): Promise<ParseResult> {
	const text = await file.text();
	const parsed = Papa.parse<Record<string, string>>(text, {
		header: true,
		skipEmptyLines: 'greedy',
		dynamicTyping: false,
		transformHeader: (h) => h.trim(),
	});

	if (parsed.errors.length > 0) {
		const firstError = parsed.errors[0];
		throw new Error(`CSV parse error (row ${firstError.row}): ${firstError.message}`);
	}

	const headers = parsed.meta.fields?.filter(Boolean) ?? [];
	const rows: CsvRow[] = parsed.data.filter((row) =>
		Object.values(row).some((v) => v !== null && v !== undefined && String(v).trim() !== '')
	);

	// Resolve context defaults if schema fields provided
	const contextDefaults = options.fields
		? resolveContextRowDefaults([], options.fields, options.lookupData)
		: [];

	return {
		headers,
		rows,
		headerDetection: {
			headerRow: 1,
			dataStartRow: 2,
			confidence: 1,
			preHeaderRows: [],
		},
		contextRows: [],
		contextDefaults,
		sheetNames: [file.name],
		selectedSheet: file.name,
	};
}
